import { useEffect } from 'react';
import { useAuth } from '@clerk/react';
import { ClerkOfflineError } from '@clerk/react/errors';
import { apiClient } from '@/services/apiClient';

/**
 * Wires Clerk's `getToken` into the singleton `apiClient` so every
 * authenticated request picks up the current session JWT. Renders nothing.
 *
 * Lives inside the ClerkProvider tree (in App.tsx) but doesn't need to sit
 * inside any of the data contexts — it just needs to mount before the first
 * authenticated API call fires. We tear the fetcher down on unmount and on
 * sign-out so signed-out reloads send anonymous requests cleanly (useful for
 * /api/health and any other unauthenticated endpoint).
 */
export const ApiAuthBridge = () => {
  const { getToken, isLoaded, isSignedIn } = useAuth();

  useEffect(() => {
    if (!isLoaded) {
      // Clerk hasn't initialized — leave whatever fetcher is in place
      // (likely null on first mount) so anonymous endpoints still work.
      return;
    }
    // Clerk has decided: hand over the session's token fetcher (or none when signed out). This
    // also releases requests that were waiting for Clerk instead of going out without a token.
    if (isSignedIn) {
      // Use Clerk's default template — produces a short-lived session JWT
      // that the API's ClerkAuthorization verifies against Clerk's JWKS.
      // Core 3: getToken() throws ClerkOfflineError when the browser is offline (Core 2 returned
      // null). Keep the old meaning — no token — so a request fails as an ordinary network error.
      apiClient.setAuthState(async () => {
        try {
          return await getToken();
        } catch (error) {
          if (ClerkOfflineError.is(error)) return null;
          throw error;
        }
      });
    } else {
      apiClient.setAuthState(null);
    }
    return () => {
      apiClient.setTokenFetcher(null);
    };
  }, [getToken, isLoaded, isSignedIn]);

  return null;
};
